import { ReactElement, useCallback, useRef, useState } from 'react'

import { Carat, RotateClockwise, useResizeObserver } from '@syconium/little-miss-figgy'

import { trackEvent } from '../../../lib/analytics'
import { ReviewMedia } from '../ReviewsSwiperMediaSlider/ReviewsSwiperMediaSlider'

import {
  Header,
  PaginationButton,
  PaginationWrapper,
  PhotoSectionWrapper,
  Slider,
  SliderRow,
  SliderTile,
  SliderWrapper,
  Title,
} from './ReviewPhotosSection.styles'

interface ReviewPhotosSectionProps {
  tileWidthMobile: number
  tileWidthDesktop: number
  tileGapMobile: number
  tileGapDesktop: number
  renderTile(tile: ReviewMediaTile, index: number): ReactElement<unknown> | null
  tiles: readonly ReviewMediaTile[]
  titleText: string
}

export type ReviewMediaTile = {
  reviewIndex: number
  mediaIndex: number
} & ReviewMedia

export function ReviewPhotosSection({
  tileWidthMobile,
  tileWidthDesktop,
  tileGapMobile,
  tileGapDesktop,
  renderTile,
  tiles,
  titleText,
}: ReviewPhotosSectionProps): ReactElement<unknown> | null {
  const [scrollsLeft, setScrollsLeft] = useState(false)
  const [scrollsRight, setScrollsRight] = useState(false)
  const sliderRef = useRef<HTMLDivElement | null>(null)
  const scrollRight = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft += sliderRef.current.getBoundingClientRect().width * 0.6
    }
  }, [])
  const scrollLeft = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft -= sliderRef.current.getBoundingClientRect().width * 0.6
    }
  }, [])
  const checkIfScrollable = useCallback(() => {
    if (sliderRef.current && sliderRef.current.scrollWidth > sliderRef.current.clientWidth) {
      setScrollsLeft(sliderRef.current.scrollLeft > 8)
      setScrollsRight(
        sliderRef.current.scrollLeft + sliderRef.current.clientWidth + 8 <
          sliderRef.current.scrollWidth
      )
    } else {
      setScrollsLeft(false)
      setScrollsRight(false)
    }
  }, [])

  useResizeObserver(sliderRef, checkIfScrollable)

  if (tiles.length < 1) return null

  return (
    <PhotoSectionWrapper>
      <Header>
        <Title asTag='h2'>{titleText}</Title>
        <PaginationWrapper>
          <PaginationButton onClick={scrollsLeft ? scrollLeft : undefined} disabled={!scrollsLeft}>
            <Carat rotateClockwise={RotateClockwise.Half} width='12px' height='12px' />
          </PaginationButton>
          <PaginationButton
            onClick={scrollsRight ? scrollRight : undefined}
            disabled={!scrollsRight}
          >
            <Carat width='12px' height='12px' />
          </PaginationButton>
        </PaginationWrapper>
      </Header>
      <SliderWrapper>
        <Slider tabIndex={0} ref={sliderRef} onScroll={checkIfScrollable}>
          <SliderRow>
            {tiles.map((tile, index) => (
              <SliderTile
                tileGapDesktop={tileGapDesktop}
                tileGapMobile={tileGapMobile}
                mobileTileWidth={tileWidthMobile}
                desktopTileWidth={tileWidthDesktop}
                key={tile.src}
                {...trackEvent({
                  category: 'media carousel',
                  action: 'click tile',
                  value: 'image',
                })}
              >
                {renderTile(tile, index)}
              </SliderTile>
            ))}
          </SliderRow>
        </Slider>
      </SliderWrapper>
    </PhotoSectionWrapper>
  )
}
